export {Signup as Page};

import {useState, useContext} from 'preact/hooks';
import {createUserWithEmailAndPassword, sendEmailVerification} from 'firebase/auth';
import {auth} from '../../includes/firebase';
import {useLocation} from '../../hooks/useLocation.js';
import {Helmet} from "react-helmet-async";
import messages from "../../messages/messages.js";
import {NotificationContext} from '../../components/NotificationContext.jsx';
import {AccountDetail} from '../../components/AccountDetail.jsx';
import {GoogleAuthButton} from '../../components/GoogleAuthButton.jsx';
import PasswordRequirements, { isPasswordValid as validatePassword, validateAccount } from '../../components/PasswordRequirements.jsx';
import API_URL from '../../includes/api.js';
import {RingSpinner} from '../../components/Spinner.jsx';

function Signup() {

    const location = useLocation();
    const {addNotification} = useContext(NotificationContext);

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showValidationHint, setShowValidationHint] = useState(false);

    const isPasswordValid = validatePassword(password);
    const doPasswordsMatch = password.length > 0 && password === confirmPassword;
    const canCreateAccount = Boolean(email && password && confirmPassword && isPasswordValid && doPasswordsMatch && !loading);

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        setShowValidationHint(false);
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        setShowValidationHint(false);
    };

    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
        setShowValidationHint(false);
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!canCreateAccount) {
            setShowValidationHint(true);
            return;
        }

        setShowValidationHint(false);
        setLoading(true);

        // centralized signup validation (email + password fields)
        const check = validateAccount({ email, password, confirmPassword, requireEmail: true });
        if (!check.ok) {
            addNotification(check.message, 'error');
            setLoading(false);
            return;
        }

        // Check if normalized email already exists
        let email_exists = false;
        await fetch(API_URL + '/users/exists_by_normalized_email?email=' + encodeURIComponent(email))
            .then(response => response.json()).then(data => {
            if (data.code === 200) {
                email_exists = data.data;
                console.log(email_exists)
            } else {
                addNotification(data.message, 'error');
            }
        }).catch((error) => {
            addNotification(error.message, 'error');
        }).finally(() => {
            setLoading(false);
        });

        if(email_exists) {
            addNotification('An account with this email already exists.', 'error');
            setLoading(false);
            return;
        }

        await createUserWithEmailAndPassword(auth, email, password)
            .then(async (userCredential) => {
                if (userCredential && userCredential.user) {
                    try {
                        sendEmailVerification(userCredential.user, {url: "https://" + messages["DOMAIN"]});
                        addNotification('Verification email sent. Please check your inbox.', 'success');
                    } catch (err) {
                        addNotification('Failed to send verification email.', 'error');
                    }
                }
                location.route('/account');
            })
            .catch((error) => {
                addNotification(error.message, 'error');
                setLoading(false);
            });
    }

    return (
        <section className="text-white">

            <Helmet>
                <title>{messages["SITE.NAME"]} | Sign up now!</title>
                <meta name="description" content="Sign up for an account"/>
            </Helmet>

            <div className="flex flex-col md:flex-row justify-center gap-6">
                <div className="w-full border border-border/60 bg-zinc-800 rounded-lg shadow-sm md:mt-0 sm:max-w-md xl:p-0">
                    <form action="#" className="p-6 space-y-4 md:space-y-6 sm:p-8">
                        <h1 className="text-xl font-bold leading-tight tracking-tight text-white md:text-2xl mb-2">
                            Create an account
                        </h1>

                        <div className="text-xs text-gray-500">
                            Secure sign-in powered by <a href="https://firebase.google.com/products/auth"
                                                         target="_blank" className="text-gray-400 underline"
                                                         rel="noopener noreferrer">Google Firebase</a>
                        </div>


                        <div className="flex rounded-lg overflow-hidden border border-gray-400">
                            <a href="/login"
                               className="w-1/2 text-center text-white hover:bg-white hover:text-black text-sm px-5 py-2.5">Login</a>
                            <a href="/signup"
                               className="w-1/2 text-center text-white bg-gray-600/30 text-sm px-5 py-2.5 hover:bg-gray-600/50 border-l border-gray-400">Register</a>
                        </div>

                        <div>
                            <label htmlFor="email" className="block mb-2 text-sm font-medium text-white">Your
                                email</label>
                            <input type="email" name="email" id="email"
                                   value={email}
                                   onChange={handleEmailChange}
                                   className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                   placeholder="name@company.com" required=""/>
                        </div>
                        <div>
                            <label htmlFor="password"
                                   className="block mb-2 text-sm font-medium text-white">Password</label>
                            <input type="password" name="password" id="password" placeholder="••••••••"
                                   value={password}
                                   onChange={handlePasswordChange}
                                   className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                   required=""/>
                        </div>
                        <div>
                            <label htmlFor="confirmPassword" className="block mb-2 text-sm font-medium text-white">Confirm Password</label>
                            <input type="password" name="confirmPassword" id="confirmPassword" placeholder="••••••••"
                                   value={confirmPassword}
                                   onChange={handleConfirmPasswordChange}
                                   className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                   required=""/>
                        </div>

                        <PasswordRequirements password={password} />

                        <button type="submit" onClick={onSubmit}
                                disabled={loading}
                                aria-describedby={showValidationHint && !canCreateAccount ? "signup-validation-hint" : undefined}
                                className={`w-full mb-4 font-medium rounded-lg text-sm px-5 py-2.5 text-center border ${canCreateAccount ? "cursor-pointer text-white border-gray-400 hover:bg-white hover:text-black" : "cursor-not-allowed text-gray-400 border-gray-700 opacity-70"}`}>Create
                            account {loading && <RingSpinner/>}
                        </button>

                        <GoogleAuthButton
                            email={email}
                            disabled={loading}
                            label="Sign up with Google"
                        />

                        {showValidationHint && !canCreateAccount && (
                            <div id="signup-validation-hint" className="mb-4 rounded-lg border border-zinc-700 bg-zinc-900/60 p-3 text-xs text-gray-300">
                                Please check your details: fill in email, use a password that meets all rules above, and make sure both passwords match.
                            </div>
                        )}

                        <p className="mb-0 text-xs text-gray-400">By creating an account, you agree to our <a href="/privacy" className="text-primary-500 hover:underline">Privacy
                            Policy</a>.</p>
                    </form>
                </div>

                <AccountDetail />
            </div>
        </section>
    );
}
